import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import {
  PlanetaryHour,
  calculateAllPlanetaryHours,
} from "../utils/planetaryHours";

interface UsePlanetaryHourReturn {
  currentPlanetaryHour: PlanetaryHour | null;
  countdown: string;
  progress: number;
}

export const usePlanetaryHour = (
  sunrise: Date | null,
  sunset: Date | null,
  referenceDate: Date = new Date()
): UsePlanetaryHourReturn => {
  const [currentPlanetaryHour, setCurrentPlanetaryHour] =
    useState<PlanetaryHour | null>(null);
  const [countdown, setCountdown] = useState<string>("");
  const [progress, setProgress] = useState<number>(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const nextHourTimerRef = useRef<NodeJS.Timeout | null>(null);
  const currentHourRef = useRef<PlanetaryHour | null>(null);

  // Pre-calculate all planetary hours when inputs change
  const allPlanetaryHours = useMemo(() => {
    if (sunrise && sunset) {
      return calculateAllPlanetaryHours(sunrise, sunset, referenceDate);
    }
    return [];
  }, [sunrise, sunset, referenceDate]);

  // Function to find the current planetary hour from pre-calculated hours
  const findCurrentHour = useCallback(
    (hours: PlanetaryHour[], time: Date = new Date()): PlanetaryHour | null => {
      if (hours.length === 0) return null;

      const currentTime = time.getTime();
      return (
        hours.find(
          (hour) =>
            currentTime >= hour.startTime.getTime() &&
            currentTime < hour.endTime.getTime()
        ) || null
      );
    },
    []
  );

  // Format countdown text
  const formatCountdown = useCallback(
    (minutesLeft: number, secondsLeft: number): string => {
      if (minutesLeft > 0) {
        return `(în ${minutesLeft} minute)`;
      } else {
        return `(în ${secondsLeft} secunde)`;
      }
    },
    []
  );

  // Calculate progress percentage of current hour
  const calculateProgress = useCallback((hour: PlanetaryHour): number => {
    const now = new Date().getTime();
    const start = hour.startTime.getTime();
    const end = hour.endTime.getTime();
    const total = end - start;
    const elapsed = now - start;

    return Math.min(Math.max(Math.floor((elapsed / total) * 100), 0), 100);
  }, []);

  // Force an immediate update of the countdown display
  const forceCountdownUpdate = useCallback((endTime: Date): void => {
    // Get fresh time calculation for right now
    const now = new Date();
    const timeLeftMs = endTime.getTime() - now.getTime();

    let minutesLeft = 0;
    let secondsLeft = 0;

    if (timeLeftMs > 0) {
      // Calculate basic minutes and seconds left
      minutesLeft = Math.floor(timeLeftMs / 60000);
      secondsLeft = Math.floor((timeLeftMs % 60000) / 1000);

      // Check if we're at the exact target second - when seconds match
      const isAtTargetSecond = now.getSeconds() === endTime.getSeconds();

      // If we're at the target second, explicitly subtract one minute
      // This ensures we show the next minute down at the exact second
      if (isAtTargetSecond && minutesLeft > 0) {
        minutesLeft -= 1;
        secondsLeft = 0; // Reset seconds since we're at the exact target second
      }
    }

    // Set the countdown text (without debug seconds)
    const newCountdown = formatCountdown(minutesLeft, secondsLeft);
    if (countdown !== newCountdown) {
      setCountdown(newCountdown);
    }

    // Update progress if we have a current hour
    if (currentHourRef.current) {
      setProgress(calculateProgress(currentHourRef.current));
    }
  }, [formatCountdown, countdown, calculateProgress, setCountdown, setProgress]);

  // Main function to manage countdown updates
  const manageCountdownUpdates = useCallback((endTime: Date): void => {
    // Clear any existing timer
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    // Step 1: Force an immediate update
    forceCountdownUpdate(endTime);

    // Step 2: Calculate when the next update should occur
    const now = new Date();
    const timeLeftMs = endTime.getTime() - now.getTime();

    if (timeLeftMs <= 0) return; // No need to schedule updates if time's up

    // Get the target second from the end time
    const targetSecond = endTime.getSeconds();
    const currentSecond = now.getSeconds();
    const currentMs = now.getMilliseconds();

    // Calculate time until next update
    let msUntilNextUpdate: number;
    const minutesLeft = Math.floor(timeLeftMs / 60000);

    if (minutesLeft > 0) {
      // Calculate seconds until we hit the target second
      let secondsToWait = targetSecond - currentSecond;

      // If we're at or past the target second, wait until next minute
      if (secondsToWait <= 0) {
        secondsToWait += 60;
      }

      msUntilNextUpdate = secondsToWait * 1000 - currentMs;
    } else {
      // Less than a minute left, update every second
      msUntilNextUpdate = 1000 - currentMs;
    }

    // Step 3: Schedule the next update
    timerRef.current = setTimeout(() => {
      manageCountdownUpdates(endTime);
    }, msUntilNextUpdate);
  }, [forceCountdownUpdate]);

  // Update current hour and set up the next timer
  const updateCurrentHour = useCallback(() => {
    if (allPlanetaryHours.length === 0) return;

    const hour = findCurrentHour(allPlanetaryHours);

    // Only update the state if the hour has changed
    if (
      hour &&
      (!currentPlanetaryHour ||
        hour.planet !== currentPlanetaryHour.planet ||
        hour.startTime.getTime() !== currentPlanetaryHour.startTime.getTime())
    ) {
      // Reset progress to zero first to ensure animation occurs
      setProgress(0);

      // Update the current hour ref and state
      currentHourRef.current = hour;
      setCurrentPlanetaryHour(hour);

      // Use setTimeout to ensure the progress reset is processed before updating to correct value
      setTimeout(() => {
        setProgress(calculateProgress(hour));
      }, 50);
    } else if (hour) {
      // No hour change, just update the ref
      currentHourRef.current = hour;
    }

    if (hour) {
      // Start managing countdown updates with new end time
      manageCountdownUpdates(hour.endTime);

      // Calculate time until next hour change
      const timeUntilNextHour = hour.endTime.getTime() - Date.now();

      // Clear any existing hour change timer
      if (nextHourTimerRef.current) clearTimeout(nextHourTimerRef.current);

      // Set up timer for next hour change
      if (timeUntilNextHour > 0) {
        nextHourTimerRef.current = setTimeout(() => {
          updateCurrentHour();
        }, timeUntilNextHour + 100); // Add 100ms buffer
      }
    }
  }, [allPlanetaryHours, findCurrentHour, currentPlanetaryHour, setProgress, setCurrentPlanetaryHour, calculateProgress, manageCountdownUpdates]);

  // Main effect to initialize and update timers
  useEffect(() => {
    if (sunrise && sunset && allPlanetaryHours.length > 0) {
      updateCurrentHour();
    }

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (nextHourTimerRef.current) clearTimeout(nextHourTimerRef.current);
    };
  }, [sunrise, sunset, allPlanetaryHours, updateCurrentHour]);

  // Set up countdown timer when current hour changes
  useEffect(() => {
    if (currentPlanetaryHour) {
      // Ensure progress is updated immediately when hour changes
      setTimeout(() => {
        setProgress(calculateProgress(currentPlanetaryHour));
      }, 50);

      manageCountdownUpdates(currentPlanetaryHour.endTime);
    }

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [currentPlanetaryHour, manageCountdownUpdates, calculateProgress]);

  return { currentPlanetaryHour, countdown, progress };
};
